import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { BarChart3, Loader2, CircleDollarSign, Cpu, Users, Rocket, CalendarDays, CheckCircle2, Gavel, AlertTriangle, Activity, ArrowRightCircle } from "lucide-react";

const Stat = ({ label, value, icon: Icon }) => (
  <div className="m-card p-5"><div className="flex items-center justify-between"><p className="mono-label">{label}</p><Icon size={15} className="text-[#2563EB]" /></div><p className="font-display text-3xl font-extrabold text-white mt-2">{value}</p></div>
);

const Bars = ({ title, data, emptyText }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const max = Math.max(...entries.map((e) => e[1]), 0.0001);
  return (
    <div className="m-card p-5">
      <p className="mono-label mb-4">{title}</p>
      {entries.length === 0 ? <p className="text-sm text-[#64748B]">{emptyText}</p> : (
        <div className="space-y-3">{entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-3"><span className="text-xs text-[#94A3B8] w-32 truncate">{k}</span><div className="flex-1 h-2 rounded-full bg-[#0B0F19] overflow-hidden"><div style={{ width: `${(v / max) * 100}%` }} className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF]" /></div><span className="text-xs text-white font-mono w-16 text-right">${v}</span></div>
        ))}</div>
      )}
    </div>
  );
};

export default function Analytics() {
  const [d, setD] = useState(null);
  const { t, lang } = useI18n();
  const L = (id, en) => lang === "id" ? id : en;
  useEffect(() => { api.analytics().then(setD); }, []);
  if (!d) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;
  return (
    <div className="space-y-6">
      <div>
        <p className="mono-label">{t("an.label")}</p>
        <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><BarChart3 className="text-[#2563EB]" /> {t("an.title")}</h1>
        <p className="text-sm text-[#94A3B8] mt-1">{t("an.subtitle")}</p>
      </div>
      {d.weekly_operating_review && (() => {
        const w = d.weekly_operating_review;
        const completedCount = (w.completed_work?.tasks?.length || 0) + (w.completed_work?.missions?.length || 0);
        const decisionCount = (w.founder_decisions?.resolved_approvals?.length || 0) + (w.founder_decisions?.decisions?.length || 0);
        const attentionCount = (w.current_attention?.tasks?.length || 0) + (w.current_attention?.pending_approvals?.length || 0);
        return (
          <section className="m-card p-5 space-y-4" data-testid="weekly-operating-review">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="mono-label flex items-center gap-2"><CalendarDays size={14} className="text-[#38BDF8]" /> WEEKLY OPERATING REVIEW</p>
                <h2 className="font-display text-xl font-bold text-white mt-1">
                  {L("Review operasional 7 hari terakhir", "Last 7 days operating review")}
                </h2>
                <p className="text-xs text-[#64748B] mt-1">
                  {new Date(w.window.start).toLocaleDateString()} — {new Date(w.window.end).toLocaleDateString()} · {L("berdasarkan state nyata, tanpa progress % buatan", "based on real state, without synthetic progress %")}
                </p>
              </div>
              <span className="m-pill text-[10px]">ROLLING 7 DAYS</span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                [CheckCircle2, L("Selesai", "Completed"), completedCount],
                [Gavel, L("Keputusan Founder", "Founder Decisions"), decisionCount],
                [AlertTriangle, L("Perlu perhatian sekarang", "Needs Attention Now"), attentionCount],
                [Activity, L("Perubahan material", "Material Changes"), w.material_changes?.length || 0],
              ].map(([Icon, label, value]) => (
                <div key={label} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Icon size={14} className="text-[#60A5FA]" />
                    <span className="font-mono text-xl font-bold text-white">{value}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-2">{label}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4">
                <p className="mono-label mb-3">{L("HASIL MINGGU INI", "THIS WEEK OUTPUT")}</p>
                <div className="space-y-2">
                  {[...(w.completed_work?.tasks || []), ...(w.completed_work?.missions || [])].slice(0, 6).map((x) => (
                    <div key={x.id} className="text-xs">
                      <p className="text-[#E2E8F0]">{x.title || x.objective || x.id}</p>
                      <p className="text-[10px] text-[#64748B] mt-0.5">{x.project_code || "COMPANY"} · COMPLETED</p>
                    </div>
                  ))}
                  {completedCount === 0 && <p className="text-xs text-[#64748B]">{L("Belum ada completion production yang tercatat dalam window ini.", "No production completion recorded in this window.")}</p>}
                </div>
              </div>

              <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4">
                <p className="mono-label mb-3">{L("FOCUS BERIKUTNYA", "NEXT WEEK FOCUS")}</p>
                <div className="space-y-2">
                  {(w.next_week_focus || []).slice(0, 6).map((x, idx) => (
                    <div key={`${x.project_code}-${idx}`} className="flex gap-2 text-xs">
                      <ArrowRightCircle size={13} className="text-[#60A5FA] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[#E2E8F0]">{x.focus}</p>
                        <p className="text-[10px] text-[#64748B] mt-0.5">{x.project_code}{x.blocker ? ` · blocker: ${x.blocker}` : ""}</p>
                      </div>
                    </div>
                  ))}
                  {!(w.next_week_focus || []).length && <p className="text-xs text-[#64748B]">{L("Belum ada next action/milestone yang tercatat.", "No next action/milestone is recorded.")}</p>}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("an.statSpend")} value={`$${d.total_cost}`} icon={CircleDollarSign} />
        <Stat label={t("an.statRuns")} value={d.total_runs} icon={Cpu} />
        <Stat label={t("an.statMissions")} value={`${d.missions_completed}/${d.missions_total}`} icon={Rocket} />
        <Stat label={t("an.statWorkforce")} value={`${d.agents_active}/${d.agents_total}`} icon={Users} />
      </div>
      <div className="m-card p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="cost-accuracy-panel">
        <div>
          <p className="mono-label text-[9px]">{t("an.accuracy")}</p>
          <p className="text-xs text-white mt-1">{t("an.meteredRuns")} <span className="font-semibold">{d.provider_metered_runs}</span> · {t("an.estimatedRuns")} <span className="font-semibold">{d.estimated_runs}</span></p>
        </div>
        <div className="text-xs text-[#94A3B8]">
          Token: <span className="text-white font-mono">{(d.total_input_tokens || 0).toLocaleString()}</span> {t("an.tokensIn")} / <span className="text-white font-mono">{(d.total_output_tokens || 0).toLocaleString()}</span> {t("an.tokensOut")}
        </div>
        <p className="text-[11px] text-[#64748B]">{t("an.accuracyNote")}</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Bars title={t("an.byProject")} data={d.by_project} emptyText={t("an.noCost")} />
        <Bars title={t("an.byModel")} data={d.by_model} emptyText={t("an.noCost")} />
        <Bars title={t("an.byEmployee")} data={d.by_agent} emptyText={t("an.noCost")} />
      </div>
    </div>
  );
}
